import {Text} from '@mantine/core';
import React, {useMemo} from 'react';

import useAntibodyUIState from 'antibodies/hooks/useAntibodyUIState';
import {Permissions} from 'auth/PermissionConstants';
import {MODEL_RUN_SOURCE} from 'constants/AnalyticsConstants';
import {isAaSequenceNode} from 'graphql-client/aaSequenceTypenames';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import StructurePredictionForm from 'model-hub/scientific-models/input-forms/structure-prediction/StructurePredictionForm';
import {useScientificModelRegistry} from 'model-hub/scientific-models/useAvailableScientificModels';
import {CenteredSpinner} from 'ui-library/Spinner';

import {
  ProteinEntityStructurePredictionQuery,
  ProteinEntityStructurePredictionQueryVariables,
} from './__generated/ProteinEntityStructurePredictionQuery';
import {PROTEIN_ENTITY_STRUCTURE_PREDICTION_QUERY} from './ProteinEntityStructurePredictionQuery';

type AntibodyVersionNode = NonNullable<
  ExtractByTypename<ProteinEntityStructurePredictionQuery['antibody'], 'AntibodyNode'>['version']
>;

type ChainEntity = AntibodyVersionNode['chains'][number]['entity'];

type ProteinChainEntity = ExtractByTypename<ChainEntity, 'ProteinNode' | 'AaSequenceNode'>;

function areAllProteinChainEntities(entities: Array<ChainEntity>): entities is Array<ProteinChainEntity> {
  return entities.every((entity) => isAaSequenceNode(entity));
}

export interface ProteinEntityStructureTabViewProps {
  proteinId: string;
}

export default function ProteinEntityStructureTabView({
  proteinId,
}: ProteinEntityStructureTabViewProps): JSX.Element {
  const {
    state: {openVersionId},
  } = useAntibodyUIState(proteinId);
  const queryResult = useSkippableBenchlingQuery<
    ProteinEntityStructurePredictionQuery,
    ProteinEntityStructurePredictionQueryVariables
  >(PROTEIN_ENTITY_STRUCTURE_PREDICTION_QUERY, {
    variables: {proteinId, versionId: openVersionId!},
    skip: openVersionId == null,
  });
  const scientificModelRegistryResult = useScientificModelRegistry();

  const multimerCapableModels = useMemo(() => {
    if (scientificModelRegistryResult.type !== 'ready') {
      return [];
    }
    return scientificModelRegistryResult.registry.availableStructurePredictionModels.filter(
      (model) => model.capabilities.supportsMultimers
    );
  }, [scientificModelRegistryResult]);

  if (
    openVersionId == null ||
    queryResult.type === QueryResultType.PENDING ||
    scientificModelRegistryResult.type === 'loading'
  ) {
    return <CenteredSpinner />;
  }

  if (queryResult.type === QueryResultType.ERROR || scientificModelRegistryResult.type === 'error') {
    return (
      <Text p="lg" size="sm">
        Error when loading structure prediction form. Please refresh the page.
      </Text>
    );
  }

  if (queryResult.type !== QueryResultType.SUCCESS || queryResult.data == null) {
    return <CenteredSpinner />;
  }

  const {bioentity, antibody} = queryResult.data;
  if (isInaccessible(bioentity) || isInaccessible(antibody)) {
    return (
      <Text p="lg" size="sm">
        This protein entity is not accessible for structure prediction.
      </Text>
    );
  }

  if (bioentity.__typename !== 'BasicFolderItemNode') {
    return (
      <Text p="lg" size="sm">
        This entity does not support structure prediction.
      </Text>
    );
  }

  const version = antibody.version;
  if (version == null) {
    return (
      <Text p="lg" size="sm">
        No protein version is available for structure prediction.
      </Text>
    );
  }

  // Any inaccessible or non-AA-sequence chain entity means we can neither read its residues nor run a
  // prediction on it; the backend rejects the whole batch if any component is inaccessible, so mirror
  // that here.
  const chainEntities = version.chains.map((chain) => chain.entity);
  if (!areAllProteinChainEntities(chainEntities)) {
    return (
      <Text p="lg" size="sm">
        Components of this protein entity are not accessible for structure prediction.
      </Text>
    );
  }

  // Each chain definition represents one or more physical chain positions (its `refs`); the backend
  // hydrates one prediction component per chain position, so repeat each chain's residues by its
  // multiplicity to match the component/residue counts the run will actually use.
  const chains = chainEntities.flatMap((entity, index) =>
    version.chains[index].refs.map(() => entity.experimentalCurrentVersion.aminoAcids)
  );
  const inputPermissions: Permissions = {
    admin: false,
    appendable: false,
    writable: false,
    readable: chainEntities.every((entity) => entity.permissions.readable),
    structurePredictionEditable: chainEntities.every((entity) =>
      Boolean(entity.permissions.structurePredictionEditable)
    ),
  };

  return (
    <StructurePredictionForm
      availableModels={multimerCapableModels}
      inputComponent={{id: proteinId, folderId: bioentity.folder.id, permissions: inputPermissions, chains}}
      source={MODEL_RUN_SOURCE.STRUCTURE_TAB}
    />
  );
}
